import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, Field, inputCls, SubmitBtn } from "@/components/student/AuthShell";
import { PasswordInput } from "@/components/student/PasswordInput";
import { apiError, useStudentAuth } from "@/lib/student-auth";

export default function StudentLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const prefill = params.get("email") || "";
  const { login } = useStudentAuth();
  const [form, setForm] = useState({ email: prefill, password: "", remember: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  const resendAccess = async () => {
    if (!form.email) {
      setError("Enter your email first, then tap resend.");
      return;
    }
    setResending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/student/resend-access`, {
        email: form.email,
      });
      toast.success("Done — check your inbox for your access link.");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not resend right now"));
    } finally {
      setResending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const s = await login(form.email, form.password, form.remember);
      toast.success(`Welcome back, ${s.name?.split(" ")[0] || "student"}!`);
      nav("/student/dashboard");
    } catch (err) {
      const msg = apiError(err?.response?.data?.detail, "Could not log you in");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testid="student-login-page"
      eyebrow="Student login"
      title="Welcome back."
      subtitle={
        prefill
          ? "Your account is ready. Log in with the password you created."
          : "Log in to continue your courses, training and certifications."
      }
      footer={
        <>
          New here?{" "}
          <Link to="/student/register" data-testid="link-to-register" className="text-[#06B6D4] font-medium">
            Create your account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} data-testid="student-login-form">
        <Field label="Email ID">
          <input
            data-testid="login-email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Password">
          <PasswordInput
            testid="login-password"
            required
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls}
            placeholder={prefill ? "Your password" : "••••••••"}
          />
        </Field>

        <div className="flex items-center justify-between mb-5">
          <label className="inline-flex items-center gap-2 text-xs text-slate-400">
            <input
              data-testid="login-remember"
              type="checkbox"
              checked={form.remember}
              onChange={(e) => setForm({ ...form, remember: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-white/20 bg-white/10"
            />
            Remember me for 30 days
          </label>
          <Link to="/student/forgot-password" data-testid="link-forgot-password" className="text-xs text-[#06B6D4]">
            Forgot password?
          </Link>
        </div>

        {error && (
          <div data-testid="login-error" className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-200">
            {error}
          </div>
        )}

        <SubmitBtn loading={loading} testid="login-submit">
          Login to Dashboard
        </SubmitBtn>

        <button
          type="button"
          onClick={resendAccess}
          disabled={resending}
          data-testid="login-resend-access"
          className="mt-3 w-full text-center text-xs text-slate-400 hover:text-[#06B6D4] transition-colors disabled:opacity-60"
        >
          {resending ? "Sending…" : "Didn't receive email? Resend access details"}
        </button>
      </form>
    </AuthShell>
  );
}
